import { message } from "antd";
import { tableService } from "../services/tables";
import { isNoAnswer } from "../services/offline/serverReach";
import { readCopy, saveCopy } from "../services/offline/offlineCopy";

const tablesCopyName = () => `tables:${localStorage.getItem("shopId")}`;

const namespace = "tables";
const initialState = {
  list: [],
  loading: {
    fetchList: false,
    fetchMore: false,
    create: false,
    addItemsToTable: false,
  },
  fetchListFailed: false,
  total: 0,
  draftOrders: [],
  currentOrder: null,
};

export const create = (data) => ({
  type: `${namespace}/create`,
  payload: data,
});

// no internet when the POS opens: the tables saved on this device
export const loadSavedTables = () => ({
  type: `${namespace}/loadSaved`,
});

export const fetchList = (params) => ({
  type: `${namespace}/fetchList`,
  payload: params,
});

export const setDraftOrders = (order) => ({
  type: `${namespace}/setDraftOrders`,
  payload: order,
});

export const replaceDraftOrders = (orders) => ({
  type: `${namespace}/replaceDraftOrders`,
  payload: orders,
});

export const setCurrentOrder = (order) => ({
  type: `${namespace}/setCurrentOrder`,
  payload: order,
});

export const addItemsToTable = (data) => ({
  type: `${namespace}/addItemsToTable`,
  payload: data,
});

export const removeItemFromTable = (data) => ({
  type: `${namespace}/removeItemFromTable`,
  payload: data,
});

export const updateItem = (data) => ({
  type: `${namespace}/updateItem`,
  payload: data,
});

export const moveTable = (data) => ({
  type: `${namespace}/moveTable`,
  payload: data,
});

export default {
  namespace,
  state: initialState,
  effects: {
    *removeItemFromTable({ payload }, { call, put }) {
      try {
        const response = yield call(tableService.removeItemFromTable, payload);

        if (response.data) {
          yield put({
            type: "setCurrentOrder",
            payload: response.data,
          });
          message.success("Item removed from table successfully");
        }
      } catch (error) {
        message.error(error.message);
      }
    },
    *addItemsToTable({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "addItemsToTable" });
      let order = null;
      try {
        const response = yield call(tableService.addItemsToTable, payload);

        if (response.data) {
          order = response.data;
          yield put({
            type: "setCurrentOrder",
            payload: response.data,
          });
          message.success("Items added to table successfully");
        }
      } catch (error) {
        message.error(error.message);
      }
      yield put({ type: "stopLoading", loadingType: "addItemsToTable" });
      return order;
    },
    // returns true when the line was changed
    *updateItem({ payload }, { call }) {
      try {
        yield call(tableService.updateItem, payload);
        return true;
      } catch (error) {
        message.error(error.message || "Not changed. Please try again.");
        return false;
      }
    },
    // returns true when the bill was moved
    *moveTable({ payload }, { call }) {
      try {
        yield call(tableService.moveTable, payload);
        return true;
      } catch (error) {
        message.error(error.message || "Not moved. Please try again.");
        return false;
      }
    },
    *create({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "create" });
      try {
        const response = yield call(tableService.create, payload);
        yield put({
          type: "updateList",
          payload: response.data,
        });
        if (response.data) {
          message.success("Table created successfully");
        }
        yield put({ type: "stopLoading", loadingType: "create" });
        return response.data;
      } catch (error) {
        message.error(error.message);
      }
      yield put({ type: "stopLoading", loadingType: "create" });
    },
    *fetchCurrentOrder({ payload }, { call, put }) {
      yield put({
        type: "setCurrentOrder",
        payload: payload,
      });
    },
    *fetchOrder({ payload }, { call, put }) {
      yield put({
        type: "setDraftOrders",
        payload: payload,
      });
    },
    *loadSaved(_, { put }) {
      const copy = readCopy(tablesCopyName());
      if (copy) yield put({ type: "setList", payload: copy });
    },
    *fetchList({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "fetchList" });
      try {
        const response = yield call(tableService.fetchList, payload);
        const tables = {
          list: response.data.list,
          total: response.data.total,
        };
        // the whole list only (not a search), kept for offline use
        if (!payload) saveCopy(tablesCopyName(), tables);
        yield put({ type: "setList", payload: tables });
        yield put({ type: "setFetchListFailed", payload: false });
      } catch (error) {
        console.error("Error fetching tables:", error);
        // no internet: the list saved on this device (busy tables may be out of date)
        const copy = isNoAnswer(error) ? readCopy(tablesCopyName()) : undefined;
        if (copy) yield put({ type: "setList", payload: copy });
        yield put({ type: "setFetchListFailed", payload: !copy });
      }
      yield put({ type: "stopLoading", loadingType: "fetchList" });
    },
  },
  reducers: {
    updateList(state, { payload }) {
      return {
        ...state,
        list: [...state.list, payload],
        total: state.total + 1,
      };
    },
    setCurrentOrder(state, { payload }) {
      return {
        ...state,
        currentOrder: payload,
      };
    },
    setList(state, { payload }) {
      return {
        ...state,
        list: payload.list,
        total: payload.total,
      };
    },
    setDraftOrders(state, { payload }) {
      if (!payload?.id) {
        return state;
      }

      const existingOrderIndex = state.draftOrders.findIndex(
        (order) => order.id === payload.id,
      );

      if (existingOrderIndex === -1) {
        return {
          ...state,
          draftOrders: [...state.draftOrders, payload],
        };
      }

      const draftOrders = [...state.draftOrders];
      draftOrders[existingOrderIndex] = payload;

      return {
        ...state,
        draftOrders,
      };
    },
    replaceDraftOrders(state, { payload }) {
      const draftOrders = payload.reduce((orders, order) => {
        if (order?.id && !orders.some(({ id }) => id === order.id)) {
          orders.push(order);
        }
        return orders;
      }, []);

      return {
        ...state,
        draftOrders,
      };
    },
    setFetchListFailed(state, { payload }) {
      return { ...state, fetchListFailed: payload };
    },
    startLoading(state, { loadingType }) {
      return { ...state, loading: { ...state.loading, [loadingType]: true } };
    },
    stopLoading(state, { loadingType }) {
      return { ...state, loading: { ...state.loading, [loadingType]: false } };
    },
  },
};
